import React, { useState, useCallback, useEffect } from 'react';
import { Title, useGetOne, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';

import { getCurrentPartner } from '@src/providers/dataProvider/shared/partner';
import type { PartnerSettings } from '@src/settings';

import {
  cancelCatalogUpload,
  scheduleCatalogUpload,
  getCatalogUploads,
  getCatalogUploadDownloadUrl,
  getCatalogUploadUrl,
  uploadCatalogFileToS3,
  validateCatalogUpload,
} from '@src/apis/reware/catalog';
import type { CatalogUploadListItem } from '@src/apis/reware/catalog';
import type { CatalogUploadValidation } from '@src/apis/reware/catalog/validateCatalogUpload';

import CsvDropZone from './CsvDropZone';
import ConfirmUploadDialog from './ConfirmUploadDialog';
import UploadHistoryTable from './UploadHistoryTable';
import ValidationResults from './ValidationResults';
import { CreateCatalogUploadTemplateDialog } from './createCatalogUploadTemplate';
import type { UploaderPageState, UploadOptions } from './types';

interface LoadingBackdropProps {
  open: boolean;
  title: string;
  subtitle?: string;
}

const LoadingBackdrop: React.FC<LoadingBackdropProps> = ({ open, title, subtitle }) => (
  <Backdrop
    open={open}
    sx={{
      color: '#fff',
      zIndex: (theme): number => theme.zIndex.drawer + 1,
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <CircularProgress color="inherit" size={60} />
    <Typography variant="h6">{title}</Typography>
    {subtitle && <Typography variant="body2">{subtitle}</Typography>}
  </Backdrop>
);

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const CatalogUploaderPage: React.FC = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const partner = getCurrentPartner();
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });

  // Page state machine
  const [pageState, setPageState] = useState<UploaderPageState>('idle');

  // File selection state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  // Validation state
  const [validation, setValidation] = useState<CatalogUploadValidation | null>(null);
  const [uploadOptions, setUploadOptions] = useState<UploadOptions | null>(null);
  const [validationId, setValidationId] = useState<string | null>(null);
  const [awsKey, setAwsKey] = useState<string | null>(null);
  const [uploadFilename, setUploadFilename] = useState<string | null>(null);

  // Preview pagination state
  const [previewPageSize, setPreviewPageSize] = useState(50);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);

  // Upload history state
  const [uploads, setUploads] = useState<CatalogUploadListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  const loadUploads = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    try {
      const result = await getCatalogUploads(partner.id, page, pageSize);
      setUploads(result.catalogUploads.rows ?? []);
      setTotalCount(result.catalogUploads.count ?? 0);
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to load upload history'), { type: 'error', autoHideDuration: 8000 });
    } finally {
      setLoading(false);
    }
  }, [partner?.id, page, pageSize, notify]);

  useEffect(() => {
    void loadUploads();
  }, [loadUploads]);

  const handleReset = () => {
    setPageState('idle');
    setSelectedFile(null);
    setValidation(null);
    setUploadOptions(null);
    setValidationId(null);
    setAwsKey(null);
    setUploadFilename(null);
    setDialogOpen(false);
    setPreviewPageSize(50);
    setCursorHistory([null]);
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    if (pageState === 'idle') {
      setSelectedFile(null);
      setValidation(null);
    }
  };

  const handleOptionsConfirm = async (options: UploadOptions) => {
    if (!selectedFile || !partner?.code) return;

    // Extract file extension from the selected file and ensure it's applied correctly
    const ext = selectedFile.name.match(/\.(csv|tsv)$/i)?.[0] ?? '.csv';
    const cleanName = options.uploadName.replace(/\.(csv|tsv)$/i, '');
    const filename = `${cleanName}${ext}`;

    setUploadOptions(options);
    setDialogOpen(false);
    setIsValidating(true);

    try {
      const { uploadUrl, awsKey: key } = await getCatalogUploadUrl({
        partnerCode: partner.code,
        filename,
      });

      await uploadCatalogFileToS3(uploadUrl, selectedFile);

      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        source: options.uploadType,
        awsKey: key,
        filename,
        first: previewPageSize,
        after: null,
      });

      setAwsKey(key);
      setUploadFilename(filename);
      setValidation(result);
      setValidationId(result.validationId);
      setPageState('validating');
    } catch (error) {
      notify(getErrorMessage(error, 'Validation failed'), { type: 'error', autoHideDuration: 8000 });
    } finally {
      setIsValidating(false);
    }
  };

  const handleBeginUpload = async () => {
    if (!partner?.code || !uploadOptions || !awsKey || !uploadFilename) return;

    setIsUploading(true);
    try {
      const result = await scheduleCatalogUpload({
        partnerCode: partner.code,
        source: uploadOptions.uploadType,
        scheduledAt: uploadOptions.scheduledAt,
        timezone: uploadOptions.timezone,
        totalRowCount: validation?.rowCount,
        awsKey,
        filename: uploadFilename,
      });

      if (result.scheduleCatalogUpload.id) {
        notify('Upload submitted successfully', { type: 'success' });

        // Backend now returns the upload ID directly
        handleReset();
        void navigate(`/catalog/import/${result.scheduleCatalogUpload.id}`);
      } else {
        notify('Upload failed', { type: 'error', autoHideDuration: 8000 });
      }
    } catch (error) {
      notify(getErrorMessage(error, 'Upload failed'), { type: 'error', autoHideDuration: 8000 });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = async (uploadId: number) => {
    try {
      const success = await cancelCatalogUpload(uploadId);
      if (success) {
        notify('Upload cancelled', { type: 'success' });
        void loadUploads();
      } else {
        notify('Failed to cancel upload', { type: 'error', autoHideDuration: 8000 });
      }
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to cancel upload'), { type: 'error', autoHideDuration: 8000 });
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleDownloadOriginal = async (uploadId: number) => {
    try {
      const url = await getCatalogUploadDownloadUrl(uploadId, 'location');
      if (url) {
        window.open(url, '_blank');
      } else {
        notify('Download URL not available', { type: 'warning' });
      }
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to get download URL'), { type: 'error', autoHideDuration: 8000 });
    }
  };

  const handleDownloadErrorReport = async (uploadId: number) => {
    try {
      const url = await getCatalogUploadDownloadUrl(uploadId, 'errorContents');
      if (url) {
        window.open(url, '_blank');
      } else {
        notify('Error report not available', { type: 'warning' });
      }
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to get download URL'), { type: 'error', autoHideDuration: 8000 });
    }
  };

  const handleRowClick = (uploadId: number) => {
    void navigate(`/catalog/import/${uploadId}`);
  };

  const handlePreviewNextPage = async () => {
    if (!validationId || !validation?.previewRecords || !partner?.code) return;

    const endCursor = validation.previewRecords.pageInfo.endCursor;
    if (!endCursor) return;

    setIsLoadingPage(true);

    try {
      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        validationId,
        first: previewPageSize,
        after: endCursor,
      });
      setValidation(result);
      // Add the current end cursor to history for backward navigation
      setCursorHistory(prev => [...prev, endCursor]);
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to load preview records'), { type: 'error', autoHideDuration: 8000 });
    } finally {
      setIsLoadingPage(false);
    }
  };

  const handlePreviewPreviousPage = async () => {
    if (!validationId || cursorHistory.length <= 1 || !partner?.code) return;

    setIsLoadingPage(true);

    try {
      // Remove the current page's cursor from history
      const newHistory = [...cursorHistory];
      newHistory.pop();
      setCursorHistory(newHistory);

      // Use the previous cursor (which is now the last one in the history)
      const previousCursor = newHistory[newHistory.length - 1];

      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        validationId,
        first: previewPageSize,
        after: previousCursor,
      });
      setValidation(result);
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to load preview records'), { type: 'error', autoHideDuration: 8000 });
    } finally {
      setIsLoadingPage(false);
    }
  };

  const handlePreviewPageSizeChange = async (newPageSize: number) => {
    if (!validationId || !partner?.code) return;

    setPreviewPageSize(newPageSize);
    setCursorHistory([null]); // Reset cursor history when changing page size
    setIsLoadingPage(true);

    try {
      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        validationId,
        first: newPageSize,
        after: null,
      });
      setValidation(result);
    } catch (error) {
      notify(getErrorMessage(error, 'Failed to load preview records'), { type: 'error', autoHideDuration: 8000 });
    } finally {
      setIsLoadingPage(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Catalog Uploader" />

      {pageState === 'idle' && (
        <CsvDropZone
          onFileSelected={handleFileSelected}
          disabled={isUploading || isValidating}
          onError={message => notify(message, { type: 'error', autoHideDuration: 8000 })}
          onCreateCustomTemplate={() => setIsCreateTemplateOpen(true)}
        />
      )}

      {pageState === 'validating' && validation && uploadOptions && (
        <ValidationResults
          options={uploadOptions}
          validation={validation}
          onEditOptions={() => setDialogOpen(true)}
          onCancel={handleReset}
          onBeginUpload={handleBeginUpload}
          isUploading={isUploading}
          previewPageSize={previewPageSize}
          onPreviewNextPage={handlePreviewNextPage}
          onPreviewPreviousPage={handlePreviewPreviousPage}
          onPreviewPageSizeChange={handlePreviewPageSizeChange}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <UploadHistoryTable
          uploads={uploads}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          loading={loading}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onCancelUpload={handleCancelUpload}
          onDownloadOriginal={handleDownloadOriginal}
          onDownloadErrorReport={handleDownloadErrorReport}
          onRowClick={handleRowClick}
        />
      </Box>

      {selectedFile && (
        <ConfirmUploadDialog
          open={dialogOpen}
          fileName={selectedFile.name}
          onConfirm={handleOptionsConfirm}
          onCancel={handleDialogCancel}
        />
      )}

      <CreateCatalogUploadTemplateDialog
        open={isCreateTemplateOpen}
        onClose={() => setIsCreateTemplateOpen(false)}
        templateDefinitions={settings?.catalogTemplateDefinitions}
      />

      <LoadingBackdrop
        open={isValidating}
        title="Validating file..."
        subtitle="This may take a moment for large files"
      />

      <LoadingBackdrop
        open={isLoadingPage}
        title="Loading preview..."
      />
    </Box>
  );
};

export default CatalogUploaderPage;
